import {
  isCheckConflictsError,
  isMeetingMutationError,
  normalizeCheckConflictsError,
  normalizeCreateMeetingError,
  normalizeEditMeetingError,
} from "./calendarMeetingErrors"
import { httpClient } from "./httpClient"
import type {
  CalendarMeetingLookupRequest,
  CheckMeetingConflictsRequest,
  CheckMeetingConflictsResponse,
  CreateScheduledMeetingRequest,
  EditScheduledMeetingRequest,
  InvitationMeetingLookup,
  PagedResponse,
  ReportEmailNotificationRequest,
  ScheduledMeeting,
  ScheduledMeetingForHost,
  ScheduledMeetingHistoryItem,
  SchedulingLimits,
  ToggleEmailNotificationsRequest,
} from "./types"
import { formatAvatarUrl, formatUser } from "./utils"

const formatScheduledMeeting = (
  meeting: ScheduledMeeting,
): ScheduledMeeting => ({
  ...meeting,
  host: meeting.host ? formatUser(meeting.host) : meeting.host,
})

const formatScheduledMeetingForHost = (
  meeting: ScheduledMeetingForHost,
): ScheduledMeetingForHost => ({
  ...meeting,
  host: meeting.host ? formatUser(meeting.host) : meeting.host,
  participants: meeting.participants.map((p) => ({
    ...p,
    avatar: formatAvatarUrl(p.avatar),
  })),
})

const formatScheduledMeetingHistoryItem = (
  meeting: ScheduledMeetingHistoryItem,
): ScheduledMeetingHistoryItem => ({
  ...meeting,
  host: meeting.host ? formatUser(meeting.host) : meeting.host,
})

async function getUpcomingMeetings(request?: {
  fromTimestamp?: string
  count?: number
}): Promise<ScheduledMeeting[]> {
  const response = await httpClient.get<ScheduledMeeting[]>(
    "/calendar/upcoming",
    { params: request },
  )
  return response.data.map(formatScheduledMeeting)
}

async function getPastMeetings(request?: {
  cursor?: string
  limit?: number
}): Promise<PagedResponse<ScheduledMeetingHistoryItem>> {
  const response = await httpClient.get<
    PagedResponse<ScheduledMeetingHistoryItem>
  >("/calendar/past", { params: request })
  return {
    ...response.data,
    items: response.data.items.map(formatScheduledMeetingHistoryItem),
  }
}

async function getCancelledMeetings(request?: {
  cursor?: string
  limit?: number
}): Promise<PagedResponse<ScheduledMeetingHistoryItem>> {
  const response = await httpClient.get<
    PagedResponse<ScheduledMeetingHistoryItem>
  >("/calendar/cancelled", { params: request })
  return {
    ...response.data,
    items: response.data.items.map(formatScheduledMeetingHistoryItem),
  }
}

async function getMeeting(
  request: CalendarMeetingLookupRequest,
): Promise<ScheduledMeeting> {
  const response = await httpClient.get<ScheduledMeeting>(
    "/calendar/get-meeting",
    { params: request },
  )
  return formatScheduledMeeting(response.data)
}

async function getMeetingByInvitationToken(request: {
  invitationToken: string
}): Promise<InvitationMeetingLookup> {
  const response = await httpClient.get<InvitationMeetingLookup>(
    "/calendar/get-meeting-by-invitation-token",
    { params: request },
  )
  if ("meetId" in response.data && response.data.meetId) {
    return formatScheduledMeeting(response.data)
  }
  return response.data
}

async function getMeetingForHost(
  request: CalendarMeetingLookupRequest,
): Promise<ScheduledMeetingForHost> {
  const response = await httpClient.get<ScheduledMeetingForHost>(
    "/calendar/get-meeting-for-host",
    { params: request },
  )
  return formatScheduledMeetingForHost(response.data)
}

async function checkIfCanInviteEmails(request: {
  emails: string[]
}): Promise<{ blockedBy: string[] }> {
  const response = await httpClient.post<{ blockedBy: string[] }>(
    "/calendar/check-if-can-invite-emails",
    request,
  )
  return {
    blockedBy: response.data.blockedBy || [],
  }
}

async function checkSchedulingLimits(): Promise<SchedulingLimits> {
  const response = await httpClient.get<SchedulingLimits>(
    "/calendar/check-scheduling-limits",
  )
  return response.data
}

async function checkConflicts(
  request: CheckMeetingConflictsRequest,
): Promise<CheckMeetingConflictsResponse> {
  try {
    const response = await httpClient.post<CheckMeetingConflictsResponse>(
      "/calendar/check-conflicts",
      request,
    )
    return {
      busy: response.data.busy,
      externalCalendarBusy: response.data.externalCalendarBusy,
      busyEmails: response.data.busyEmails || [],
    }
  } catch (error) {
    throw normalizeCheckConflictsError(error)
  }
}

async function createMeeting(
  request: CreateScheduledMeetingRequest,
): Promise<ScheduledMeetingForHost> {
  try {
    const response = await httpClient.post<ScheduledMeetingForHost>(
      "/calendar/create-meeting",
      request,
    )
    return formatScheduledMeetingForHost(response.data)
  } catch (error) {
    throw normalizeCreateMeetingError(error)
  }
}

async function editMeeting(
  request: EditScheduledMeetingRequest,
): Promise<ScheduledMeetingForHost> {
  try {
    const response = await httpClient.post<ScheduledMeetingForHost>(
      "/calendar/edit-meeting",
      request,
    )
    return formatScheduledMeetingForHost(response.data)
  } catch (error) {
    throw normalizeEditMeetingError(error)
  }
}

async function cancelMeeting(request: { meetId: string }): Promise<void> {
  await httpClient.post("/calendar/cancel-meeting", request)
}

async function declineMeeting(request: { meetId: string }): Promise<void> {
  await httpClient.post("/calendar/decline-invitation", request)
}

async function toggleEmailNotifications(
  request: ToggleEmailNotificationsRequest,
): Promise<void> {
  await httpClient.post("/calendar/toggle-email-notifications", request)
}

async function reportInvitation(
  request: ReportEmailNotificationRequest,
): Promise<void> {
  await httpClient.post("/calendar/report-invitation", request)
}

async function unsubscribeFromEmails(
  request: ToggleEmailNotificationsRequest,
): Promise<void> {
  await httpClient.post("/calendar/unsubscribe-from-emails", request)
}

async function getMeetingIcs(
  meetId: string,
  inline?: string,
): Promise<string> {
  const response = await httpClient.get<string>(
    `/calendar/ics/${meetId}.ics`,
    { params: { inline }, responseType: "text" },
  )
  return response.data
}

export const calendarApi = {
  getMeeting,
  getMeetingByInvitationToken,
  getMeetingForHost,
  getUpcomingMeetings,
  getPastMeetings,
  getCancelledMeetings,
  checkIfCanInviteEmails,
  checkSchedulingLimits,
  checkConflicts,
  createMeeting,
  editMeeting,
  cancelMeeting,
  declineMeeting,
  getMeetingIcs,
  reportInvitation,
  toggleEmailNotifications,
  unsubscribeFromEmails,
  isMeetingMutationError,
  isCheckConflictsError,
}
